import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    CheckpointActionAlertLocationTypeOptions,
    CheckpointActionAlertRecipientTypeOptions,
} from '@fi-shared/checklist/checklist-consts';
import { CheckpointActionRecipientType, CheckpointActionType } from '@fi-shared/checklist/checklist-enum';
import { ICheckpointActionAlertRecipient } from '@fi-shared/checklist/checklist-v2';
import { IAccessRightFilterCollection, ILightWeightBusinessUnit, ILightWeightUser } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { cloneDeep } from '@fi-shared/utils';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiUserContactTypeEnum } from '@fi-shared/fi-user-contact-chip-grid-input/fi-user-contact-type.enum';
import {
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemTitleFn,
} from '@fi-app/shared/fi-select-search/fi-select-search';

@Component({
    selector: 'fi-checkpoint-action-recipient',
    templateUrl: './action-recipient.component.html',
    styleUrls: ['./action-recipient.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-action-recipient' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiCheckpointActionRecipientComponent }],
    standalone: false,
})
export class FiCheckpointActionRecipientComponent extends FiComponentsContainer implements OnInit {
    @Input() isDirectAssigneeAvailable: boolean = true;

    @Input({ required: true }) actionType: CheckpointActionType;
    @Input({ required: true }) recipient: ICheckpointActionAlertRecipient;
    @Output() recipientChange = new EventEmitter<ICheckpointActionAlertRecipient>();
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;

    CheckpointActionAlertRecipientTypeOptions: INameIdObj[] = [];

    protected isSharingItemDisabledFn: FiDropdownItemDisabledFn<ILightWeightUser | ILightWeightBusinessUnit> = (
        item: ILightWeightUser | ILightWeightBusinessUnit,
        allItems: ILightWeightUser[] | ILightWeightBusinessUnit[],
    ) => {
        return !item.IsActivated;
    };

    protected getSharingItemTitleFn: FiDropdownItemTitleFn<ILightWeightUser | ILightWeightBusinessUnit> = (
        item: ILightWeightUser | ILightWeightBusinessUnit,
        allItems: ILightWeightUser[] | ILightWeightBusinessUnit[],
    ) => {
        return !item.IsActivated ? this.appTextService.get('common.deactivated') : undefined;
    };

    protected getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ILightWeightUser | ILightWeightBusinessUnit> =
        (
            item: ILightWeightUser | ILightWeightBusinessUnit,
            allItems: ILightWeightUser[] | ILightWeightBusinessUnit[],
        ) => {
            return !item.IsActivated ? 'red-color' : undefined;
        };

    constructor(
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.setCheckpointActionAlertRecipientTypeOptions();
    }

    private setCheckpointActionAlertRecipientTypeOptions(): void {
        this.CheckpointActionAlertRecipientTypeOptions = cloneDeep(CheckpointActionAlertRecipientTypeOptions);
        if (this.isDirectAssigneeAvailable) {
            return;
        }

        this.removeCheckpointActionAlertRecipientType(CheckpointActionRecipientType.SMS);
        this.removeCheckpointActionAlertRecipientType(CheckpointActionRecipientType.EmailAddress);
    }

    private removeCheckpointActionAlertRecipientType(optionType: CheckpointActionRecipientType): void {
        const optionIndex: number = this.CheckpointActionAlertRecipientTypeOptions.findIndex(
            (option: INameIdObj): boolean => {
                return option.Id === optionType;
            },
        );
        if (optionIndex >= 0) {
            this.CheckpointActionAlertRecipientTypeOptions.splice(optionIndex, 1);
        }
    }

    protected onRecipientChange(resetRecipientData: boolean = false): void {
        if (resetRecipientData) {
            this.recipient.UserIds = [];
            this.recipient.RoleIds = [];
            this.recipient.UnitIds = [];
            this.recipient.UserContacts = [];
        }
        this.cdr.detectChanges();
        this.recipientChange.emit(this.recipient);
    }

    private isRecipientType(type: CheckpointActionRecipientType): boolean {
        return this.recipient.RecipientType === type;
    }

    protected isRolesRecipientType = () => this.isRecipientType(CheckpointActionRecipientType.Roles);
    protected isLocalUnitRolesRecipientType = () => this.isRecipientType(CheckpointActionRecipientType.RolesLocal);
    protected isUserRecipientType = () => this.isRecipientType(CheckpointActionRecipientType.Users);
    protected isEmailRecipientType = () => this.isRecipientType(CheckpointActionRecipientType.EmailAddress);
    protected isSMSRecipientType = () => this.isRecipientType(CheckpointActionRecipientType.SMS);

    protected getRecipientContactType(): FiUserContactTypeEnum {
        switch (this.recipient.RecipientType) {
            case CheckpointActionRecipientType.SMS:
                return FiUserContactTypeEnum.PhoneNumber;
            case CheckpointActionRecipientType.EmailAddress:
                return FiUserContactTypeEnum.Email;
        }
    }

    private getActionText(taskTextId: string, notificationTextId: string): string {
        return this.appTextService.get(
            this.actionType === CheckpointActionType.CreateTask ? taskTextId : notificationTextId,
        );
    }

    protected getTargetLabelText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_target_label_task',
            'checklist.checkpoint_action_target_label_notification',
        );
    }

    protected getTargetHintText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_target_hint_task',
            'checklist.checkpoint_action_target_hint_notification',
        );
    }

    protected getRolesLabelText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_recipient_roles_label_task',
            'checklist.checkpoint_action_recipient_roles_label_notification',
        );
    }

    protected getRolesSelectHintText(): string {
        const isLocalRoles = this.isLocalUnitRolesRecipientType();
        return this.getActionText(
            isLocalRoles
                ? 'checklist.checkpoint_action_recipient_local_roles_hint_task'
                : 'checklist.checkpoint_action_recipient_roles_hint_task',
            isLocalRoles
                ? 'checklist.checkpoint_action_recipient_local_roles_hint_notification'
                : 'checklist.checkpoint_action_recipient_roles_hint_notification',
        );
    }

    protected getUnitsHintText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_recipient_units_hint_task',
            'checklist.checkpoint_action_recipient_units_hint_notification',
        );
    }

    protected getUsersLabelText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_recipient_users_label_task',
            'checklist.checkpoint_action_recipient_users_label_notification',
        );
    }

    protected getUsersSelectHintText(): string {
        return this.getActionText(
            'checklist.checkpoint_action_recipient_users_hint_task',
            'checklist.checkpoint_action_recipient_users_label_notification',
        );
    }

    protected showRecipientLocationSelection(): boolean {
        return (
            this.actionType === CheckpointActionType.CreateTask &&
            this.recipient.RecipientType === CheckpointActionRecipientType.RolesLocal
        );
    }

    protected readonly CheckpointActionAlertLocationTypeOptions = CheckpointActionAlertLocationTypeOptions;
}
